import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import { useI18n } from './i18n';
import { getTranscriptSpeakers } from './transcriptUtils';
import { trackEvent } from './analytics';

export default function SpeakerNamesEditor({ data, usePostprocessing = true, onSave }) {
  const { t } = useI18n();
  const formId = useId();
  const speakers = useMemo(() => getTranscriptSpeakers(data, usePostprocessing), [data, usePostprocessing]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const openButton = useRef(null);
  const wasEditing = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(Object.fromEntries(speakers.map(speaker => [speaker.id, speaker.name])));
  }, [speakers, editing]); // Preserve typed names during background review updates.

  useEffect(() => {
    if (wasEditing.current && !editing) openButton.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  if (!speakers.length) return null;

  const save = async event => {
    event.preventDefault();
    if (saving) return;
    const changes = {};
    for (const speaker of speakers) {
      const name = (draft[speaker.id] ?? speaker.name).trim();
      if (name !== speaker.name) changes[speaker.id] = name;
    }
    if (!Object.keys(changes).length) { trackEvent('speaker_names_edit_canceled', { reason: 'no_changes' }); setEditing(false); return; }
    trackEvent('speaker_names_save_attempt', { speaker_count: Object.keys(changes).length });
    setSaving(true);
    setError('');
    try {
      await onSave(changes);
      setEditing(false);
      setSaved(true);
    } catch (failure) {
      const code = failure?.code;
      setError(t(code === 'invalid_speaker_name' ? 'overview.speakerNameInvalid'
        : code === 'unknown_speaker' ? 'overview.speakerNamesChanged' : 'overview.speakerNamesSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="speaker-names-editor" aria-label={t('overview.speakerNames')}>
      {!editing ? (
        <div className="speaker-names-toolbar">
          <button ref={openButton} type="button" className="speaker-names-open" aria-expanded="false" aria-controls={formId}
            onClick={() => { trackEvent('speaker_names_edit_opened', { speaker_count: speakers.length,
              has_uncertain_names: speakers.some(speaker => speaker.name_uncertain) }); setEditing(true); setSaved(false); setError(''); }}>
            <FiEdit2 aria-hidden="true" />{t('overview.renameSpeakers')}
          </button>
          {saved && <span className="speaker-names-saved" role="status">{t('overview.speakerNamesSaved')}</span>}
          {speakers.some(speaker => speaker.name_uncertain) && (
            <span className="speaker-names-notice">{t('overview.speakerNamesUncertain')}</span>
          )}
        </div>
      ) : (
        <form id={formId} onSubmit={save} className="speaker-names-form" onKeyDown={event => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            if (!saving) { trackEvent('speaker_names_edit_canceled', { reason: 'escape' }); setEditing(false); setError(''); }
          }
        }}>
          <p>{t('overview.speakerNamesHint')}</p>
          {speakers.map((speaker, index) => (
            <div key={speaker.id} className="speaker-name-row">
              <label htmlFor={`${formId}-${index}`}>{speaker.id}</label>
              <input id={`${formId}-${index}`} name={speaker.id} type="text" maxLength={60}
                value={draft[speaker.id] ?? speaker.name} disabled={saving} autoFocus={index === 0}
                autoComplete="off" placeholder={speaker.name}
                onChange={event => setDraft(previous => ({ ...previous, [speaker.id]: event.target.value }))} />
              {speaker.name_uncertain && speaker.name_variants?.length > 0 && (
                <span className="speaker-name-variants">{t('overview.speakerNameVariants', { names: speaker.name_variants.join(' / ') })}</span>
              )}
            </div>
          ))}
          {error && <p className="speaker-names-error" role="alert">{error}</p>}
          <div className="speaker-names-actions">
            <button type="submit" className="primary-button" disabled={saving}>
              {t(saving ? 'overview.speakerNamesSaving' : 'overview.saveSpeakerNames')}
            </button>
            <button type="button" className="secondary-button" disabled={saving}
              onClick={() => { trackEvent('speaker_names_edit_canceled', { reason: 'cancel_button' }); setEditing(false); setError(''); }}>{t('common.cancel')}</button>
          </div>
        </form>
      )}
    </section>
  );
}
